import { useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import { Box, Button, IconButton, TextField, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import ResetIcon from "@mui/icons-material/RotateLeft";
import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import SegmentedControl from "@plannel/components/ui/SegmentedControl";
import { PANEL, PANEL_LABEL_SX } from "@plannel/components/ui/SidePanel";
import { DPItemAutocomplete, MPItemAutocomplete, RPItemAutocomplete, IPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";
import CustomerAutocomplete from "@plannel/components/filter/CustomerAutocomplete";
import companyService from "@plannel/services/data/company-service";
import itemService from "@plannel/services/data/item-service";
import customerService from "@plannel/services/data/customer-service";
import debug from "@plannel/utils/debug-util";
import codeUtil from "@plannel/pages/utils/code-util";
import colorUtil from "@plannel/pages/utils/color-util";
import Snackbar from "@plannel/components/Snackbar";
import { isEqual, isEmpty, cloneDeep } from "lodash";
import ElasticSearchUtil from "@plannel/pages/utils/elastic-search-util";
import { FS } from "@plannel/utils/font-util";

const MODEL_CODES = {
  ITEM: "ITEM",
  CUSTOMER: "CUSTOMER"
}

const attrFields = [
  "attr01", "attr02", "attr03", "attr04", "attr05", "attr06", "attr07", "attr08", "attr09", "attr10"
];

const ITEM_NAME = "itemName";
const CUSTOMER_NAME = "customerName";
const COMMON_NAME = "name";
const CUSTOMER_HRCHY = "customerHrchy";
const ITEM_HRCHY = "itemHrchy";
const INV_CLASS_CD = "invClassCd";
const CUSTOMER_CD = "customerCd";
const ITEM_CD = "itemCd";

const { filterType, module } = codeUtil;
const itemFilterModalSearchFields = [...ElasticSearchUtil.defaultItemSearchFields, ...ElasticSearchUtil.itemDescSearchFields, ...ElasticSearchUtil.itemHrchySearchFields,  ...ElasticSearchUtil.itemAttributeSearchFields];

/*
 * 조건 행 — Claude Design "PlanNEL Side Panels" 3b 안.
 *
 * 예전에는 조건을 `"품목 명=로드바이크"` 한 줄짜리 Chip 으로 그렸다. 필드·연산자·값이 한 덩어리
 * 문자열이라 값이 길면 통째로 잘렸고, 삭제도 그 라벨 문자열로 되짚어 찾아야 했다.
 * 지금은 (필드 / 연산자 / 값) 세 칸으로 나눠 값만 말줄임하고, 삭제는 저장 키+값으로 직접 지운다.
 *
 * 연산자 배지는 **읽기 전용**이다 — = / ≠ 는 헤더의 세그먼트 하나가 필터 전체에 걸린다.
 * (시안은 행마다 다른 연산자를 두지만, 이 값은 7개 화면의 쿼리 빌더와 저장된 레이아웃이
 *  `filterType: [and|or, equal|notEqual]` 한 쌍으로 읽어 간다. 행별 연산자는 그 포맷을 넓혀야 해서
 *  이번 범위 밖이다.) 그래서 배지는 버튼처럼 보이지 않게 옅은 칩으로만 둔다.
 */
const ConditionRow = ({ field, operator, value, valid, onDelete, deleteLabel }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "8px",
      p: "7px 7px 7px 12px",
      borderRadius: "11px",
      bgcolor: valid ? PANEL.wellBg : "#fdf5f4",
      boxShadow: `inset 0 0 0 1px ${valid ? PANEL.line : "#f0c9c5"}`,
    }}
  >
    <Box
      sx={{
        flex: "0 1 auto",
        maxWidth: "45%",
        fontSize: FS.TITLE,
        color: PANEL.inkBody,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {field}
    </Box>
    <Box
      sx={{
        flex: "none",
        minWidth: 22,
        px: "6px",
        py: "1px",
        textAlign: "center",
        borderRadius: "6px",
        bgcolor: PANEL.accentSoft,
        color: PANEL.accentHover,
        fontSize: FS.CAPTION,
        fontWeight: 600,
        lineHeight: 1.6,
      }}
    >
      {operator}
    </Box>
    <Tooltip title={value}>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize: FS.TITLE,
          fontWeight: 500,
          color: valid ? PANEL.ink : PANEL.danger,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {value}
      </Box>
    </Tooltip>
    <Tooltip title={deleteLabel}>
      <IconButton
        size="small"
        onClick={onDelete}
        sx={{
          p: 0.25,
          flex: "none",
          color: PANEL.iconFaint,
          "& > svg": { fontSize: 17 },
          "&:hover": { color: PANEL.danger, bgcolor: "transparent" },
        }}
      >
        <CloseIcon />
      </IconButton>
    </Tooltip>
  </Box>
);

/* 행 사이의 조용한 연결 라벨 — 헤더의 AND/OR 세그먼트가 실제 설정이고, 여기는 그 메아리다 */
const LogicSeparator = ({ label }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "10px", px: "2px" }}>
    <Box sx={{ fontSize: FS.CAPTION, fontWeight: 600, letterSpacing: "0.08em", color: "#9dc3d2" }}>{label}</Box>
    <Box sx={{ flex: 1, height: "1px", bgcolor: PANEL.footLine }} />
  </Box>
);

/*
* filterTypes: Array / ['and', 'equal']
*/
const FilterModal = (props) => {
  const { t, close, open, modelCode, modalResultData, filteredList, filterTypes, requestModule } = props;
  const customer = codeUtil.customer;
  const { snackBarMessageColor } = colorUtil;
  const [filterText, setFilterText] = useState("");
  const [filterList, setFilterList] = useState({});
  const [displayFilterList, setDisplayFilterList] = useState([]);
  const [attrData, setAttrData] = useState({});
  const [chartTypeToggle, setChartTypeToggle] = useState("");
  const [stateToggle, setStateToggle] = useState("");
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [validationMap, setValidationMap] = useState({});
  const filterItemText = useRef("");
  const filterListRef = useRef({});
  const target = modelCode === customer.name ? "customer" : "item";
  const [reloadText, setReloadText] = useState(false);

  const isItem = String(modelCode).toUpperCase() === MODEL_CODES.ITEM;
  const operatorSymbol = filterType[chartTypeToggle]?.symbol ?? filterType.equal.symbol;
  const logicSymbol = filterType[stateToggle]?.symbol ?? filterType.or.symbol;

  /*
   * 저장 키 → 화면에 보일 필드 이름.
   * `name` 은 모델에 따라 품목명/거래처명이다 — 저장 키만 보고 t("name") 을 쓰면 조건을 추가한
   * 직후("Item Name")와 모달을 다시 연 뒤("Name")의 라벨이 달라진다.
   */
  const fieldLabelOf = (key) => {
    if (attrData[key]) {
      return attrData[key];
    }
    if (key === COMMON_NAME) {
      return t(isItem ? ITEM_NAME : CUSTOMER_NAME);
    }
    if (key.includes(CUSTOMER_HRCHY)) {
      return t(CUSTOMER_HRCHY);
    }
    if (key.includes(ITEM_HRCHY)) {
      return t(ITEM_HRCHY);
    }
    if (key.includes(INV_CLASS_CD)) {
      return t(INV_CLASS_CD);
    }
    return t(key);
  };

  /* 검색 결과의 속성 이름 → filterList 에 실제로 쌓이는 키 */
  const storeKeyOf = (key) => {
    if (key === ITEM_NAME || key === CUSTOMER_NAME) {
      return COMMON_NAME;
    }
    if (key.includes(CUSTOMER_HRCHY)) {
      return CUSTOMER_HRCHY;
    }
    if (key.includes(ITEM_HRCHY)) {
      return ITEM_HRCHY;
    }
    if (key.includes(INV_CLASS_CD)) {
      return INV_CLASS_CD;
    }
    return key;
  };

  const makeDisplayItem = (storeKey, value, valid = true) => ({
    storeKey,
    value,
    field: fieldLabelOf(storeKey),
    valid,
  });

  const refreshFilterList = () => {
    filterItemText.current = "";
    filterListRef.current = {};
    setFilterList({});
    setDisplayFilterList([]);
    setValidationMap({});
    setChartTypeToggle(filterType.equal.cd);
    setStateToggle(filterType.or.cd)
    setFilterText("");
  }

  const handleClose = () => {
    refreshFilterList();
    close({});
  };

  const convertKey = (lists) => {
    const displayList = [];
    if (Object.keys(lists).length > 0 && Object.keys(attrData).length > 0) {
      for (const key of Object.keys(lists)) {
        for (const val of lists[key]) {
          if (isEmpty(lists[key])) {
            continue;
          }
          displayList.push(makeDisplayItem(key, val, validationMap[`${key}:${val}`] !== false));
        }
      }
      setDisplayFilterList(displayList)
    } else {
      setDisplayFilterList([])
    }
  }

  const setGridInfo = obj => {
    const objData = {}
    if (obj && obj?.[target]) {
      const targetObject = obj[target];
      for (const attrField of attrFields) {
        if (targetObject?.[attrField]) {
          const value = targetObject[attrField];
          objData[attrField] = value
        } else {
          objData[attrField] = t(attrField)
        }
      }
    } else {
      for (const attrField of attrFields) {
        objData[attrField] = t(attrField)
      }
    }
    setAttrData(objData)
  }

  const getCustomConfig = () => {
    companyService.getCustomConfig()
      .then(response => {
        setGridInfo(response.data);
      })
      .catch(e => {
        debug(e);
      });
  }

  const handleConfirm = () => {
    modalResultData({
      modelCode,
      filterList,
      filterType: [stateToggle, chartTypeToggle]
    });
    setFilterText("");
    filterItemText.current = "";
    close(modalResultData);
  };

  const onChangeSearchCode = e => {
    const filterTextInput = e.target.value;
    setFilterText(filterTextInput);
  };

  const handleFilterText = () => {
    if (!filterText) {
      return;
    }
    for (const [key, value] of Object.entries(filterText)) {
      if (filterItemText.current === value) {
        const objList = Object.keys(filterList).length > 0 ? { ...filterList } : {};
        const filterKey = storeKeyOf(key);

        if (value) {
          if (objList[filterKey]) {
            if (objList[filterKey].includes(value)) {
              setSnackInfo({ open: true, severity: snackBarMessageColor.warning, content: t("MSG_DuplicateData") });
              break;
            }
            objList[filterKey] = [...objList[filterKey], value];
          } else {
            objList[filterKey] = [value];
          }
        }

        setDisplayFilterList(prev => [...prev, makeDisplayItem(filterKey, value)]);
        filterListRef.current = objList;
        setFilterList(objList);
        setFilterText("");
        break;
      }
    }
  };

  /*
   * 삭제는 라벨 문자열이 아니라 (저장 키, 값) 으로 한다.
   * 예전에는 `"품목 명=로드바이크"` 를 `=` 로 쪼개 되짚었기 때문에, 값 안에 `=` 가 있거나
   * 서로 다른 필드에 같은 값이 들어 있으면 엉뚱한 줄이 함께 지워졌다.
   */
  const handleDelete = (storeKey, value) => () => {
    setDisplayFilterList(prev => prev.filter(row => !(row.storeKey === storeKey && row.value === value)));
    setFilterList(prev => {
      const next = { ...prev };
      const rest = (next[storeKey] ?? []).filter(v => v !== value);
      if (rest.length > 0) {
        next[storeKey] = rest;
      } else {
        delete next[storeKey];
      }
      return next;
    });
  };

  const addPastedCodes = (codes, cdKey) => {
    // 중복 제거: display label이 아닌 filterListRef의 실제 코드값 기준 + trim 정규화 (비동기 race condition 방지)
    const existingCodes = new Set((filterListRef.current[cdKey] || []).map(c => c.trim()));
    const uniqueCodes = [...new Set(codes.map(c => c.trim()))].filter(code => !existingCodes.has(code));

    if (uniqueCodes.length < codes.length) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.warning, content: t("MSG_DuplicateData") });
    }
    if (uniqueCodes.length === 0) return;

    // API 호출 전 ref를 즉시 선반영 → 연속 붙여넣기 시 중복 방지
    filterListRef.current = {
      ...filterListRef.current,
      [cdKey]: [...(filterListRef.current[cdKey] || []), ...uniqueCodes]
    };

    const validateApi = cdKey === ITEM_CD ? getItemValidateApi() : customerService.validateCodes;

    validateApi(uniqueCodes)
      .then(response => {
        const validSet = new Set(response?.data ?? []);

        setValidationMap(prev => {
          const updated = { ...prev };
          uniqueCodes.forEach(code => { updated[`${cdKey}:${code}`] = validSet.has(code); });
          return updated;
        });
        setFilterList(prev => ({ ...prev, [cdKey]: [...(prev[cdKey] || []), ...uniqueCodes] }));
        setDisplayFilterList(prev => [
          ...prev,
          ...uniqueCodes.map(code => makeDisplayItem(cdKey, code, validSet.has(code)))
        ]);
      })
      .catch(e => {
        // API 실패 시 선반영한 ref를 롤백하여 이후 붙여넣기 중복 오판 방지
        filterListRef.current = {
          ...filterListRef.current,
          [cdKey]: (filterListRef.current[cdKey] || []).filter(code => !uniqueCodes.includes(code))
        };
        debug(e);
      });
  };

  const getItemValidateApi = () => {
    switch (requestModule) {
      case module.IP:
        return itemService.validateCodesByIP;
      case module.MP:
        return itemService.validateCodesByMP;
      case module.RP:
        return itemService.validateCodesByRP;
      case module.DP:
        return itemService.validateCodesByDP;
      default:
        return itemService.validateCodes;
    }
  };

  /* 검색 입력은 조건 목록과 같은 폭으로 눕는다 — 예전 220px 고정폭은 모달 폭이 늘어난 지금 어색하다 */
  const SEARCH_SX = { width: "100%", minWidth: 0, mr: 0 };

  const renderDefaultSearchFilter = () => {
    return (
      <TextField fullWidth id="fullWidth" size="small" placeholder={t("search")}
        sx={{ textAlign: "left" }}
        onChange={onChangeSearchCode} />
    )
  };

  useEffect(() => {
    filterListRef.current = cloneDeep(filterList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterList]);

  useEffect(() => {
    if (reloadText) {
      convertKey(filterList);
    }
    setReloadText(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadText]);

  useEffect(() => {
    handleFilterText();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterText]);

  const renderCustomerSearchFilter = () => {
    return (
      <CustomerAutocomplete retrieve={null}
        searchValue={filterItemText}
        setSelectedCustomer={setFilterText}
        sx={SEARCH_SX}
        searchIcon={false}
        onMultiplePaste={(codes) => addPastedCodes(codes, CUSTOMER_CD)}
      />
    )
  };

  const renderItemSearchFilter = () => {
    if (isEqual(requestModule, module.RP)) {
      return (
        <RPItemAutocomplete retrieve={null}
          searchFields={itemFilterModalSearchFields}
          searchValue={filterItemText}
          setSelectedItem={setFilterText}
          sx={SEARCH_SX}
          searchIcon={false}
          onMultiplePaste={(codes) => addPastedCodes(codes, ITEM_CD)}
        />
      )
    }

    if (isEqual(requestModule, module.MP)) {
      return (
        <MPItemAutocomplete retrieve={null}
          searchFields={itemFilterModalSearchFields}
          searchValue={filterItemText}
          setSelectedItem={setFilterText}
          sx={SEARCH_SX}
          searchIcon={false}
          onMultiplePaste={(codes) => addPastedCodes(codes, ITEM_CD)}
        />
      )
    }


    if (isEqual(requestModule, module.IP)) {
      return (
        <IPItemAutocomplete retrieve={null}
          searchFields={itemFilterModalSearchFields}
          searchValue={filterItemText}
          setSelectedItem={setFilterText}
          sx={SEARCH_SX}
          searchIcon={false}
          onMultiplePaste={(codes) => addPastedCodes(codes, ITEM_CD)}
        />
      )
    }

    return (
      <DPItemAutocomplete retrieve={null}
        searchFields={itemFilterModalSearchFields}
        searchValue={filterItemText}
        setSelectedItem={setFilterText}
        sx={SEARCH_SX}
        searchIcon={false}
        onMultiplePaste={(codes) => addPastedCodes(codes, ITEM_CD)}
      />
    )
  };

  const searchFilterHandle = (param) => {
    if (String(param)?.toUpperCase() === MODEL_CODES.ITEM) {
      return renderItemSearchFilter()
    } else if (String(param)?.toUpperCase() === MODEL_CODES.CUSTOMER) {
      return renderCustomerSearchFilter()
    }
    return renderDefaultSearchFilter()
  }

  useEffect(() => {
    if (open && filteredList) {
      getCustomConfig();
      const newFilterList = filteredList[modelCode] ?? {};
      filterListRef.current = newFilterList;
      setFilterList(newFilterList);
      setStateToggle(filterTypes?.[modelCode]?.[0] ?? filterType.or.cd)
      setChartTypeToggle(filterTypes?.[modelCode]?.[1] ?? filterType.equal.cd)

      // 모달 재 오픈시 하이라이트 유지
      const itemCodes = newFilterList[ITEM_CD] || [];
      const customerCodes = newFilterList[CUSTOMER_CD] || [];
      if (itemCodes.length > 0 || customerCodes.length > 0) {
        const newMap = {};
        const promises = [];
        if (itemCodes.length > 0) {
          promises.push(
            getItemValidateApi()(itemCodes)
              .then(res => {
                const validSet = new Set(res?.data ?? []);
                itemCodes.forEach(code => { newMap[`${ITEM_CD}:${code}`] = validSet.has(code); });
              })
              .catch(() => { itemCodes.forEach(code => { newMap[`${ITEM_CD}:${code}`] = true; }); })
          );
        }
        if (customerCodes.length > 0) {
          promises.push(
            customerService.validateCodes(customerCodes)
              .then(res => {
                const validSet = new Set(res?.data ?? []);
                customerCodes.forEach(code => { newMap[`${CUSTOMER_CD}:${code}`] = validSet.has(code); });
              })
              .catch(() => { customerCodes.forEach(code => { newMap[`${CUSTOMER_CD}:${code}`] = true; }); })
          );
        }
        Promise.all(promises).then(() => {
          setValidationMap(newMap);
          setReloadText(true);
        });
      } else {
        setValidationMap({});
      }
    } else {
      setSnackInfo({ open: false, severity: "", content: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    convertKey(filterList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attrData]);

  return (
    /* 라벨·아이콘은 t("confirm") + DoneAllIcon — handleConfirm 은 부모에 필터를 넘기고 닫을 뿐
       서버에 저장하지 않는다(이 파일의 API 호출은 조회·검증뿐). 저장이 아니면 confirm 이 맞다. */
    <BaseConfigModal
      open={open}
      onClose={handleClose}
      title={isEqual(modelCode, "Item") ? t("itemFilter") : t("customerFilter")}
      subtitle={`${displayFilterList.length} ${t("filter")}`}
      titleAdornment={
        <ToggleComponent
          chartTypeToggle={chartTypeToggle}
          setChartTypeToggle={setChartTypeToggle}
          stateToggle={stateToggle}
          setStateToggle={setStateToggle}
          reloadText={setReloadText}
        />
      }
      /*
       * 초기화는 시안대로 푸터 좌측. 라벨을 단 텍스트 버튼이다 — Measure Setting·Home Card 등
       * 다른 설정 모달의 푸터가 전부 이 모양이라, 여기만 아이콘 하나면 옆의 CANCEL·CONFIRM 과
       * 무게가 어긋나고 무엇을 되돌리는 버튼인지도 hover 해야 알 수 있다.
       */
      extraActions={
        <Button size="small" startIcon={<ResetIcon />} onClick={refreshFilterList}>
          {t("reset")}
        </Button>
      }
      onSave={handleConfirm}
      saveLabel={t("confirm")}
      saveIcon={<DoneAllIcon />}
      width={452}
    >
        {/*
         * 조건 목록. 비어 있어도 높이를 유지한다 — 조건을 하나 넣을 때마다 모달이 튀어 오르면
         * 아래의 검색창(방금 타이핑하던 자리)이 커서 밑에서 사라진다.
         */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "6px",
            minHeight: 148,
            maxHeight: 320,
            overflowY: "auto",
            pr: displayFilterList.length > 0 ? "2px" : 0,
          }}
        >
          {displayFilterList.length === 0 ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                borderRadius: "11px",
                boxShadow: `inset 0 0 0 1px ${PANEL.footLine}`,
                color: PANEL.inkMuted,
                fontSize: FS.SUB,
              }}
            >
              <FilterAltOutlinedIcon sx={{ fontSize: 26, color: PANEL.iconFaint }} />
              {t("MSG_NoData")}
            </Box>
          ) : (
            displayFilterList.map((row, idx) => (
              <Box key={`${row.storeKey}:${row.value}`}>
                {idx > 0 && <LogicSeparator label={logicSymbol} />}
                <Box sx={{ mt: idx > 0 ? "6px" : 0 }}>
                  <ConditionRow
                    field={row.field}
                    operator={operatorSymbol}
                    value={row.value}
                    valid={row.valid}
                    deleteLabel={t("delete")}
                    onDelete={handleDelete(row.storeKey, row.value)}
                  />
                </Box>
              </Box>
            ))
          )}
        </Box>

        {/* 조건을 더하는 자리 — 목록과 hairline 하나로 나눈다(셸 구분선과 겹치지 않게 본문 안에서만) */}
        <Box sx={{ mt: "14px", pt: "14px", borderTop: `1px solid ${PANEL.footLine}` }}>
          <Box sx={{ ...PANEL_LABEL_SX, mb: "8px" }}>{t("search")}</Box>
          {searchFilterHandle(modelCode)}
        </Box>

        <Snackbar
          open={snackInfo.open}
          onClose={() => setSnackInfo(prev => ({ ...prev, open: false }))}
          severity={snackInfo.severity}
          content={snackInfo.content}
        />
    </BaseConfigModal>
  )
}

/*
 * 헤더의 두 세그먼트 — AND/OR(조건을 어떻게 잇는가) + = / ≠ (조건을 어떻게 비교하는가).
 * 앱 공용 SegmentedControl 을 쓴다. 시안의 세그먼트 알약과 같은 룩이면서, 화면 곳곳의
 * 다른 세그먼트와도 한 벌로 보인다(ToggleButtonGroup 을 여기서 다시 칠하지 않는다).
 */
const ToggleComponent = ({ chartTypeToggle, setChartTypeToggle, stateToggle, setStateToggle, reloadText = false }) => {
  const toggleHandler = (newAlignment) => {
    if (newAlignment) {
      setChartTypeToggle(newAlignment);
      reloadText(true)
    }
  }

  const stateToggleHandler = (newAlignment) => {
    if (newAlignment) {
      setStateToggle(newAlignment);
      reloadText(true)
    }
  }

  return (
    <Box component="span" sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
      <SegmentedControl
        value={stateToggle}
        onChange={stateToggleHandler}
        options={[
          { value: filterType.and.cd, label: filterType.and.symbol },
          { value: filterType.or.cd, label: filterType.or.symbol },
        ]}
      />
      <SegmentedControl
        value={chartTypeToggle}
        onChange={toggleHandler}
        options={[
          { value: filterType.equal.cd, label: filterType.equal.symbol },
          { value: filterType.notEqual.cd, label: filterType.notEqual.symbol },
        ]}
      />
    </Box>
  )
}

export default withTranslation()(FilterModal);
export const DPFilterModal = withTranslation()((props) => {
  return <FilterModal {...props} requestModule={module.DP} />
});
export const RPFilterModal = withTranslation()((props) => {
  return <FilterModal {...props} requestModule={module.RP} />
});
export const MPFilterModal = withTranslation()((props) => {
  return <FilterModal {...props} requestModule={module.MP} />
});
export const IPFilterModal = withTranslation()((props) => {
  return <FilterModal {...props} requestModule={module.IP} />
});